import { Button } from "@/components/ui/button";
import Loading from "@/components/fragments/Loadin";
import Error from "@/components/fragments/Error";
import { useCustomersDetail, useDeleteCustomer } from "../hooks/useCustomers";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

export default function CustomerDetailComponent({ id, email }: any) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const deleteMutation = useDeleteCustomer(email);
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const {
    data: customer,
    isLoading,
    error,
  } = useCustomersDetail({
    email,
    id: Number(id),
  });

  if (isLoading) {
    return (
      <Loading fullScreen message="Loading Detail Customer..." size="large" />
    );
  }

  if (error) {
    return <Error error={error} fullScreen showBackButton />;
  }

  if (!customer) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="w-full min-h-screen flex items-center justify-center">
          <div className="text-center bg-[#efecfa]  p-8 rounded-lg max-w-md">
            <div className="text-6xl mb-4">🔍</div>
            <div className="text-lg text-[#37393d] dark:text-white mb-6">
              Customer tidak ditemukan
            </div>
            <Link to="/customer">
              <Button
                variant="outline"
                className="border-[#1e1e1e] text-[#1e1e1e] dark:text-white hover:bg-[#efecff]"
              >
                Kembali ke Daftar Customer
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);

    try {
      await deleteMutation.mutate(Number(id));

      toast.success("Customer berhasil dihapus");
      queryClient.invalidateQueries({ queryKey: ["customers", email] });

      setTimeout(() => {
        navigate("/customer");
      }, 1500);
    } catch (error) {
      toast.error("Terjadi kesalahan saat menghapus customer");
      console.error("deleteCustomer error:", error);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header Card */}
      <div className="bg-[#f4f2fc] dark:bg-[#1b1b1b] rounded-3xl p-8 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center">
            <span className="text-4xl">👤</span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#1e1e1e] dark:text-white">
              {customer.cust_name}
            </h1>
            <p className="text-[#37393d] dark:text-white mt-1">
              Customer ID: {customer.cust_id}
            </p>
          </div>
        </div>
      </div>

      {/* Detail Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* NIK Card */}
        <div className=" rounded-2xl p-6 hover:bg-white transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0">
              <span className="text-xl">🆔</span>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-[#37393d] dark:text-white mb-1">
                NIK
              </label>
              <p className="text-lg font-semibold text-[#1e1e1e] dark:text-white">
                {customer.cust_nik}
              </p>
            </div>
          </div>
        </div>

        {/* Store ID Card */}
        <div className=" rounded-2xl p-6 hover:bg-white transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full  flex items-center justify-center shrink-0">
              <span className="text-xl">🏪</span>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-[#37393d] dark:text-white mb-1">
                Store ID
              </label>
              <p className="text-lg font-semibold text-[#1e1e1e] dark:text-white">
                {customer.store_id}
              </p>
            </div>
          </div>
        </div>

        {/* Email Card */}
        <div className=" rounded-2xl p-6 hover:bg-white transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0">
              <span className="text-xl">📧</span>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-[#37393d] dark:text-white mb-1">
                Email
              </label>
              <a
                href={`mailto:${customer.cust_email}`}
                className="text-lg font-semibold text-[#1e1e1e] dark:text-white hover:text-[#37393d] dark:text-white underline decoration-wavy underline-offset-4 decoration-[#d7d0fe] break-all"
              >
                {customer.cust_email}
              </a>
            </div>
          </div>
        </div>

        {/* Phone Card */}
        <div className="rounded-2xl p-6  transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0">
              <span className="text-xl">📱</span>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-[#37393d] dark:text-white mb-1">
                No. Telepon
              </label>
              <a
                href={`tel:${customer.cust_phone}`}
                className="text-lg font-semibold text-[#1e1e1e] dark:text-white hover:text-[#37393d] dark:text-white underline decoration-wavy underline-offset-4 decoration-[#d7d0fe]"
              >
                {customer.cust_phone}
              </a>
            </div>
          </div>
        </div>

        {/* Address Card - Full width */}
        <div className=" rounded-2xl p-6 md:col-span-2  transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-white rounded-full  flex items-center justify-center shrink-0">
              <span className="text-xl">📍</span>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-[#37393d] dark:text-white mb-1">
                Alamat
              </label>
              <p className="text-lg font-semibold text-[#1e1e1e] dark:text-white">
                {customer.cust_address}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <Link to={`/customer/edit/${customer.cust_id}`}>
          <Button
            className="hover:text-white hover:bg-[#1e1e1e]  font-semibold"
            variant={"secondary"}
          >
            ✏️ Edit Customer
          </Button>
        </Link>
        <Button
          onClick={handleDelete}
          variant="destructive"
          className="text-white hover:text-[#1e1e1e] dark:text-white hover:bg-[#efecfa] font-semibold"
        >
          🗑️ Hapus Customer
        </Button>
        {/* <Button
          variant="outline"
          className="border-[#1e1e1e] text-[#1e1e1e] dark:text-white hover:bg-[#efecfa] font-semibold"
        >
          📊 Riwayat Transaksi
        </Button> */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={handleCloseDeleteModal}
            />
            <div className="relative bg-white rounded-2xl p-6 w-[90%] max-w-sm shadow-2xl z-10 animate-in fade-in zoom-in duration-200">
              <h2 className="text-xl font-bold text-gray-900">
                Hapus Customer?
              </h2>
              <p className="text-sm text-gray-600 mt-3">
                Data customer <strong>{customer.cust_name}</strong> akan dihapus
                secara permanen dan tidak dapat dikembalikan.
              </p>
              <div className="flex gap-3 mt-6">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={handleCloseDeleteModal}
                >
                  Tidak
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 font-semibold"
                  onClick={handleConfirmDelete}
                >
                  Ya, Hapus
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
